import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { createHead } from '@unhead/vue/client';
import createGpCorePinia from 'gpcore-apikit/store/pinia';
import { createApp } from 'vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import Keycloak from 'keycloak-js';
import E2E from 'gpcore-uikit/plugins/e2e';
import * as Sentry from '@sentry/vue';
import ConfigPlugin from './plugins/config';
import App from './App.vue';
import type Config from './config';
import router from './router';
import Upload from './plugins/upload';
import Status from './plugins/status';
import 'gpcore-uikit/styles/tailwind.css';
import '@gportal/uikit/dist/styles/components/gp-brand-image.css';
import GPortalUIKitPlugin from '@gportal/uikit';

library.add(fas);
library.add(fab);

fetch('/config/panel.json').then((resp) => {
  const app = createApp(App);
  const head = createHead();
  const json = resp.json() as Promise<Config>;

  json.then((config) => {
    const keycloak = new Keycloak({
      ...config.keycloak,
    });

    Sentry.init({
      app,
      dsn: config.sentry.dsn,
      environment: config.environment,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      enabled: config.environment !== 'DEVELOPMENT',
      attachProps: true,
      attachStacktrace: true,
      replaysOnErrorSampleRate: config.environment === 'DEVELOPMENT' ? 0.0 : 1.0,
      replaysSessionSampleRate: 0.0,
      tracesSampleRate: config.environment === 'DEVELOPMENT' ? 1.0 : 0.05,
      profilesSampleRate: config.environment === 'DEVELOPMENT' ? 1.0 : 0,
    });

    keycloak
      .init({
        onLoad: 'check-sso',
        enableLogging: config.environment === 'DEVELOPMENT',
        checkLoginIframe: false,
      })
      .then(() => {
        app
          .use(ConfigPlugin, { config })
          .use(createGpCorePinia(config.api.grpc, keycloak))
          .use(router)
          .use(head)
          .use(VueReCaptcha, {
            siteKey: config.recaptcha.publicKey,
            loaderOptions: {
              autoHideBadge: true,
            },
          })
          .use(Upload)
          .use(Status, {
            endpoint: 'https://status.gpcore.io/api/v2',
            immediate: true,
            interval: 60 * 1000,
          })
          .use(GPortalUIKitPlugin, {
            useToasts: true,
            toastOptions: {
              positions: ['top-right', 'bottom-right'],
              defaultTimeout: config.environment === 'DEVELOPMENT' ? 0 : 5000,
            },
          })
          .use(E2E)
          .mount('#app');
      });
  });
});
